import { saveAs } from 'file-saver';

import { BenchmarkInterestRateView } from '../../generated/model/benchmarkInterestRateView';
import { BenchwiseView } from '../../generated/model/benchwiseView';
import {
  BenchmarkCompanyFilter,
  BenchmarkCompanyPage,
  BenchmarkResultsInputRequest,
  BenchmarkResultsView,
  BenchmarkView,
  BenchwiseSearchwiseResponse,
  CloneBenchmarkRequest,
  CreateBenchmarkRequest,
  EditNameRequest,
  RefinementSummary,
  ReportFieldConfiguration,
  ResolveBenchmarkCompanyRequest,
  SearchwiseFeedbackRequest,
  SearchwiseRequest,
  ShareBenchmarksRequest,
  UpdateRefinementRequest,
} from '../../generated/model/models';
import { OverrideInterestRateRequest } from '../../generated/model/overrideInterestRateRequest';
import { httpClient } from '../config/httpClient';
import { parseFilename } from '../utils/axios.utils';
import { makeUrl } from '../utils/routes.utils';

export const path = {
  fetchBenchmarks: '/benchmark',
  createBenchmark: '/benchmark',
  fetchBenchmarkDetails: '/benchmark/{id}',
  editBenchmark: '/benchmark/{id}/update-name',
  cloneBenchmark: '/benchmark/{id}/clone',
  fetchBenchmarkCompanies: '/benchmark/{id}/companies',
  resolveBenchmarkCompany: '/benchmark/{id}/companies/{companyId}/resolve',
  fetchBenchmarkResults: '/benchmark/{id}/results',
  fetchBenchmarkResultsInputs: '/benchmark/{id}/results-inputs',
  fetchInterestRates: '/benchmark/{id}/interest-rates',
  overrideInterestRate: '/benchmark/{id}/interest-rates/override',
  downloadResultsReport: '/benchmark/{id}/report',
  downloadResultsCustomReport: '/benchmark/{id}/custom-report',
  deleteSelected: '/benchmark',
  fetchComments: '/benchmark/{id}/comments',
  updateRefinementId: '/benchmark/{id}/update-refinement',
  fetchRefinementSummary: '/benchmark/{id}/refinement-summary',
  applyRefinement: '/benchmark/{id}/apply-refinement',
  downloadTestedPartyTemplate: '/benchmark/tested-party-template',
  testedParty: '/benchmark/{id}/tested-party',
  assignCompanyAsTestedParty: '/benchmark/{id}/tested-party/assign/{companyId}',
  initBenchwise: '/benchwise/init',
  benchwiseFastValidate: '/benchwise/{id}/fast-validate',
  benchwiseVerifywiseValidate: '/benchwise/{id}/verifywise',
  benchwiseReprocess: '/benchwise/{id}/reprocess',
  latestBenchwiseReport: '/benchmark/{id}/latest-benchwise-report',
  resetBenchwiseReport: '/benchwise/{id}/reset-benchwise',
  shareBenchmarks: '/benchmark/share',
};

export const benchwiseSearchwise = async (
  req: SearchwiseRequest,
): Promise<BenchwiseSearchwiseResponse> => {
  const response = await httpClient.post<BenchwiseSearchwiseResponse>('/benchwise/searchwise', req);
  return response.data;
};

export const benchwiseSearchwiseFeedbackUp = async (reqId: string): Promise<string | null> => {
  try {
    const req: SearchwiseFeedbackRequest = {
      feedback: 'LIKE',
      searchwiseRequestUid: reqId,
    };
    const response = await httpClient.post<string>('/benchwise/searchwise/feedback', req);
    return response.data;
  } catch {
    return null;
  }
};

export const benchwiseSearchwiseFeedbackDown = async (
  reqId: string,
  comment?: string,
): Promise<string | null> => {
  try {
    const req: SearchwiseFeedbackRequest = {
      feedback: 'DISLIKE',
      searchwiseRequestUid: reqId,
      comment: comment,
    };
    const response = await httpClient.post<string>('/benchwise/searchwise/feedback', req);
    return response.data;
  } catch {
    return null;
  }
};

export const fetchBenchmarks = async (benchwiseOnly?: boolean): Promise<BenchmarkView[]> => {
  const params = benchwiseOnly ? { benchwiseOnly: true } : {};
  const response = await httpClient.get<BenchmarkView[]>(path.fetchBenchmarks, { params });
  return response.data;
};

export const createBenchmark = async (req: CreateBenchmarkRequest): Promise<string> => {
  const response = await httpClient.post<string>(path.createBenchmark, req);
  return response.data;
};

export const fetchBenchmarkDetails = async (id: string): Promise<BenchmarkView> => {
  const response = await httpClient.get<BenchmarkView>(makeUrl(path.fetchBenchmarkDetails, { id }));
  return response.data;
};

export const editBenchmark = async (id: string, req: EditNameRequest): Promise<string> => {
  const response = await httpClient.post<string>(makeUrl(path.editBenchmark, { id }), req);
  return response.data;
};

export const updateRefinementSearchId = async (
  id: string,
  req: UpdateRefinementRequest,
): Promise<string> => {
  const response = await httpClient.post<string>(makeUrl(path.updateRefinementId, { id }), req);
  return response.data;
};

export const cloneBenchmark = async (id: string, req: CloneBenchmarkRequest): Promise<string> => {
  const response = await httpClient.post<string>(makeUrl(path.cloneBenchmark, { id }), req);
  return response.data;
};

export const fetchBenchmarkResults = async (
  id: string,
  req: BenchmarkResultsInputRequest,
): Promise<BenchmarkResultsView> => {
  const response = await httpClient.post<BenchmarkResultsView>(
    makeUrl(path.fetchBenchmarkResults, { id }),
    req,
  );
  return response.data;
};

export const fetchBenchmarkResultsInputs = async (
  id: string,
): Promise<BenchmarkResultsInputRequest> => {
  const response = await httpClient.get<BenchmarkResultsInputRequest>(
    makeUrl(path.fetchBenchmarkResultsInputs, { id }),
  );
  return response.data;
};

export const fetchCompanies = async (id: string, params: any): Promise<BenchmarkCompanyPage> => {
  const response = await httpClient.get<BenchmarkCompanyPage>(
    makeUrl(path.fetchBenchmarkCompanies, { id }),
    { params },
  );
  return response.data!;
};

export const resolveCompany = async (
  id: string,
  companyId: string,
  req: ResolveBenchmarkCompanyRequest,
): Promise<string> => {
  const response = await httpClient.post<string>(
    makeUrl(path.resolveBenchmarkCompany, { id, companyId }),
    req,
  );
  return response.data;
};

export const initBenchwise = async (req: string): Promise<any> => {
  const requestData = { studyDescription: req };
  const response = await httpClient.post<any>(makeUrl(path.initBenchwise, {}), requestData);
  return response.data;
};

export const benchwiseFastValidate = async (benchmarkId: string, req: string): Promise<number> => {
  const requestData = { studyDescription: req, applyToAll: true };
  const response = await httpClient.post<any>(
    makeUrl(path.benchwiseFastValidate, { id: benchmarkId }),
    requestData,
  );
  return response.data;
};

export const benchwiseValidate = async (
  benchmarkId: string,
  req: string,
  applyToAll: boolean,
  analyzeWebsite: boolean,
): Promise<number> => {
  const requestData = { studyDescription: req, applyToAll, analyzeWebsite };
  const response = await httpClient.post<any>(
    makeUrl(path.benchwiseVerifywiseValidate, { id: benchmarkId }),
    requestData,
  );
  return response.data;
};

export const benchwiseReprocess = async (
  benchmarkId: string,
  req: string,
): Promise<BenchwiseView> => {
  const requestData = { studyDescription: req };
  const response = await httpClient.post<any>(
    makeUrl(path.benchwiseReprocess, { id: benchmarkId }),
    requestData,
  );
  return response.data;
};

export const loadLatestBenchwiseReport = async (id: string): Promise<BenchwiseView> => {
  const response = await httpClient.get<any>(makeUrl(path.latestBenchwiseReport, { id }), {});
  return response.data;
};

export const assignCompanyAsTestedParty = async (
  id: string,
  companyId: string,
): Promise<string> => {
  const response = await httpClient.post<string>(
    makeUrl(path.assignCompanyAsTestedParty, { id, companyId }),
  );
  return response.data;
};

export const downloadReport = async (id: string, request: ReportFieldConfiguration) => {
  const endpoint = makeUrl(path.downloadResultsReport, { id });
  const response = await httpClient.post<Blob>(endpoint, request, { responseType: 'blob' });
  const blob = new Blob([response.data]);

  const filename = parseFilename(response.headers['content-disposition']);
  saveAs(blob, filename);
};

export const downloadCustomReport = async (id: string, request: ReportFieldConfiguration) => {
  const endpoint = makeUrl(path.downloadResultsCustomReport, { id });
  const response = await httpClient.post<Blob>(endpoint, request, { responseType: 'blob' });
  const blob = new Blob([response.data]);

  const filename = parseFilename(response.headers['content-disposition']);
  saveAs(blob, filename);
};

export const downloadBenchwiseDocx = async (id: string) => {
  const endpoint = `/benchwise/${id}/report-docx`;
  const response = await httpClient.get<Blob>(endpoint, { responseType: 'blob' });
  const blob = new Blob([response.data]);
  const filename =
    parseFilename(response.headers['content-disposition']) || `benchwise-report-${id}.docx`;
  saveAs(blob, filename);
};

export const deleteSelectedBenchmarks = async (
  data: string[],
  callback: () => void,
): Promise<any> => {
  await httpClient.delete(path.deleteSelected, { data });
  callback && callback();
};

export const fetchBenchmarkComments = async (id: string, params: any): Promise<string[]> => {
  const response = await httpClient.get(makeUrl(path.fetchComments, { id }), { params });
  return response.data;
};
export const fetchInterestRates = async (id: string): Promise<BenchmarkInterestRateView[]> => {
  const response = await httpClient.get(makeUrl(path.fetchInterestRates, { id }));
  return response.data;
};

export const overrideInterestRate = async (
  id: string,
  req: OverrideInterestRateRequest,
): Promise<string> => {
  const response = await httpClient.post<string>(makeUrl(path.overrideInterestRate, { id }), req);
  return response.data;
};

export const fetchRefinementSummary = async (
  id: string,
  req: BenchmarkCompanyFilter,
): Promise<RefinementSummary> => {
  const response = await httpClient.post(makeUrl(path.fetchRefinementSummary, { id }), req);
  return response.data;
};

export const applyRefinement = async (id: string, req: BenchmarkCompanyFilter): Promise<string> => {
  const response = await httpClient.post(makeUrl(path.applyRefinement, { id }), req);
  return response.data;
};

export const downloadTestedPartyTemplate = async () => {
  const response = await httpClient.get<Blob>(path.downloadTestedPartyTemplate, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data]);

  const filename = parseFilename(response.headers['content-disposition']);
  saveAs(blob, filename);
};

export const uploadTestedParty = async (
  file: Blob,
  filename: string,
  id: string,
): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file, filename);

  const config = {
    headers: {
      'content-type': 'multipart/form-data',
    },
  };

  const response = await httpClient.post(makeUrl(path.testedParty, { id }), formData, config);
  return response.data;
};

export const downloadTestedParty = async (id: string) => {
  const response = await httpClient.get<Blob>(makeUrl(path.testedParty, { id }), {
    responseType: 'blob',
  });

  const filename = parseFilename(response.headers['content-disposition']);

  return new File([response.data], filename, {
    type: response.data.type,
  });
};

export const deleteTestedParty = async (id: string) => {
  await httpClient.delete(makeUrl(path.testedParty, { id }));
};

export const shareBenchmarks = async (
  ids: string[],
  shared: boolean,
  callback: () => void,
): Promise<any> => {
  const request: ShareBenchmarksRequest = { ids, shared };
  const response = await httpClient.post(path.shareBenchmarks, request);
  callback();
  return response.data;
};

export const resetBenchwise = async (benchmarkId: string): Promise<boolean> => {
  const response = await httpClient.post(
    makeUrl(path.resetBenchwiseReport, { id: benchmarkId }),
    {},
  );
  return response.data;
};
